import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Input, Form } from 'antd';
import FormLabel from 'components/FormLabel/FormLabel';

import styled from 'styled-components/macro';

const FormItem = Form.Item;

const TYPE_INPUT = 'input';
const TYPE_TEXTAREA = 'textarea';

const sizes = {
  default: {
    className: ''
  },
  large: {
    className: 'large'
  }
};

const addRules = (requiredErrorMessage, extraRules) => {
  const rules = [];
  if (requiredErrorMessage && typeof requiredErrorMessage === 'string') {
    rules.push({
      required: true,
      message: requiredErrorMessage
    });
  }
  return [...rules, ...extraRules];
};

const generateInputPropsFromType = (type, placeholder, onChange, size) => {
  const defaultProps = {
    placeholder,
    onChange,
    className: sizes[size].className
  };
  const typeToProps = {
    [TYPE_INPUT]: {
      size
    },
    [TYPE_TEXTAREA]: {
      rows: 4
    }
  };
  const typeProps = typeToProps[type] || {};

  return {
    ...defaultProps,
    ...typeProps
  };
};

const generateInputPropsFromRules = rules => {
  return rules.reduce((newProps, rule) => {
    if (rule.max) {
      return { ...newProps, maxLength: rule.max };
    }
    return newProps;
  }, {});
};

const generateInputComponent = type => {
  const defaultInput = Input;
  const supportedInput = {
    [TYPE_INPUT]: Input,
    [TYPE_TEXTAREA]: Input.TextArea
  };
  return supportedInput[type] || defaultInput;
};

const FormInput = ({
  className,
  defaultValue,
  disabled,
  extraRules,
  extraProps,
  form,
  formLabel,
  inputType,
  label,
  onChange,
  name,
  placeholder,
  requiredErrorMessage,
  size,
  autoComplete,
  style
}) => {
  const rules = addRules(requiredErrorMessage, extraRules);
  const inputProps = {
    ...generateInputPropsFromRules(extraRules),
    ...generateInputPropsFromType(inputType, placeholder, onChange, size),
    ...extraProps
  };
  const InputComponent = generateInputComponent(inputType);

  return (
    <Fragment>
      {!formLabel && label && (
        <FormLabel size={size} className={className}>
          {label || name}
        </FormLabel>
      )}
      <FormItem label={formLabel} colon={false}>
        {form.getFieldDecorator(name, {
          rules: rules,
          initialValue: defaultValue,
          validateFirst: true
        })(<InputComponent disabled={disabled} {...inputProps} {...(autoComplete && { autoComplete: autoComplete })} style={style} />)}
      </FormItem>
    </Fragment>
  );
};

FormInput.propTypes = {
  form: PropTypes.object.isRequired,
  formLabel: PropTypes.string,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  extraRules: PropTypes.array,
  extraProps: PropTypes.object,
  inputType: PropTypes.oneOf([TYPE_INPUT, TYPE_TEXTAREA]),
  label: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  requiredErrorMessage: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(sizes)),
  style: PropTypes.object
};

FormInput.defaultProps = {
  className: '',
  defaultValue: undefined,
  extraRules: [],
  extraProps: {},
  inputType: TYPE_INPUT,
  label: '',
  onChange: undefined,
  placeholder: '',
  requiredErrorMessage: '',
  size: Object.keys(sizes)[0],
  autoComplete: undefined,
  style: {}
};

export default styled(FormInput)`
  margin-bottom: 0px;
`;

export class FormInputReadOnly extends PureComponent {
  render() {
    const { text, label } = this.props;
    return (
      <FormItem label={label} colon={false}>
        <Input value={text} disabled />
      </FormItem>
    );
  }
}

FormInputReadOnly.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired
};
